import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronUp, ArrowLeftRight, Save, AlertTriangle, Fish, BookCheck, Ban, Loader2 } from 'lucide-react';

const DAY = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'];
const PS_LABEL = { chicken: 'Chicken', beef: 'Beef', salmon: 'Salmon', sardines: 'Sardines', eggs: 'Eggs', tuna: 'Tuna', whitefish: 'White fish', shrimp: 'Prawns', turkey: 'Turkey', plant: 'Plant', other: 'Dairy / other' };

function Summary({ data }) {
  const t = data.program?.daily_target || {}, p = data.profile, v = data.program?.validation || {};
  return (
    <div className="space-y-2">
      <div className="label-mini text-[#FFB800]">Meal plan {data.state === 'pending' ? '· awaiting your review' : data.state === 'active' ? '· active (read-only here)' : ''}</div>
      {data.is_template && <div className="text-xs text-[#FF5C5C]" data-testid="nr-is-template">This is a generic template, not a personalised meal plan.</div>}
      {data.adjustment?.auto_applied && <div className="text-xs text-[#FFB800]" data-testid="nr-auto-four">Built with 4 meals instead of 3: {data.adjustment.reason}</div>}
      <div className="font-display font-bold text-lg" data-testid="nr-name">{data.program?.name}</div>
      <div className="text-xs text-muted-foreground" data-testid="nr-targets">
        Target {t.calories} kcal · Protein {t.protein} g · {data.program?.duration_weeks} weeks · Diet: {p.dietary_preference}
        {p.exclusions ? ` · Avoids: ${p.exclusions}` : ''}
      </div>
      {(v.issues || []).length > 0 && (
        <div className="rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-2.5 text-xs space-y-1" data-testid="nr-issues">
          {v.issues.slice(0, 6).map((x, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#FFB800] shrink-0" />{x}</div>)}
        </div>
      )}
    </div>
  );
}

function Frequency({ data }) {
  return (
    <div className="space-y-2" data-testid="nr-frequency">
      <div className="label-mini">Weekly protein sources & variety</div>
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <tbody>
            {data.frequency.map(f => (
              <tr key={f.week} className="border-b border-[var(--c-hair)]">
                <td className="py-1.5 pr-3 font-semibold whitespace-nowrap">Week {f.week}</td>
                <td className="py-1.5">{Object.entries(f.protein_sources).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${PS_LABEL[k] || k} ${n}×`).join(' · ')}</td>
                <td className="py-1.5 pl-3 whitespace-nowrap text-muted-foreground">{f.distinct_snacks} snack{f.distinct_snacks === 1 ? '' : 's'}</td>
                <td className="py-1.5 pl-3 whitespace-nowrap" data-testid={`nr-fish-status-${f.week}`}>{(f.fish_violations || []).length ? <span className="text-[#FF5C5C]">{f.fish_violations.length} fish rule break{f.fish_violations.length === 1 ? '' : 's'}</span> : <span className="text-[#00C896]">Fish rules OK</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted-foreground" data-testid="nr-fish-rules">Fish rules: salmon max 1×/week · sardines max 2×/week · never salmon and sardines on the same day · one fish meal per day.</div>
      {data.frequency.flatMap(f => (f.fish_violations || []).map(v => <div key={`${f.week}-${v.day_index}`} className="text-xs text-[#FF5C5C]" data-testid={`nr-fish-violation-${f.week}-${v.day_index}`}>Week {f.week} · {v.date ? format(parseISO(v.date), 'EEE d MMM') : DAY[v.day_index]}: {v.issues.join(', ')}</div>))}
      {data.snack_repetition.map(s => <div key={s.week} className="text-xs text-[#FFB800]" data-testid={`nr-snack-rep-${s.week}`}>Week {s.week}: "{s.name}" is the snack {s.times}× — add more approved snacks to the library for variety.</div>)}
      {(data.library_hints || []).map((h, i) => <div key={i} className="text-xs text-muted-foreground" data-testid={`nr-library-hint-${i}`}>• {h}</div>)}
      {Object.keys(data.library_gaps).length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="nr-library-gaps">
          Library could use more options: {Object.entries(data.library_gaps).map(([c, n]) => `${c} (${n} approved)`).join(', ')}
        </div>
      )}
    </div>
  );
}

function MealRow({ m, editable, onSave, onSwap, sid }) {
  const [open, setOpen] = useState(false);
  const [grams, setGrams] = useState(m.ingredients.map(i => i.grams));
  useEffect(() => setGrams(m.ingredients.map(i => i.grams)), [m.meal_id, m.calories]);
  return (
    <div className="rounded-lg border border-[var(--c-hair)]" data-testid={`nr-meal-${sid}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 p-2 text-left text-sm">
        <span className="w-16 shrink-0 text-[11px] text-muted-foreground">{m.slot}</span>
        <span className="flex-1 min-w-0 truncate">{m.name}</span>
        {m.from_library && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 flex items-center gap-1 ${m.source_approved ? 'border-[#00C896]/40 text-[#00C896]' : 'border-[#FFB800]/40 text-[#FFB800]'}`} data-testid={`nr-from-library-${sid}`} title={m.source_approved ? 'Copy of your approved Meal Library meal' : 'Original meal is no longer approved'}><BookCheck className="w-3 h-3" />From your Meal Library</span>}
        {['salmon', 'sardines', 'tuna', 'whitefish'].includes(m.protein_source) && <Fish className="w-3.5 h-3.5 text-[#00C8FF] shrink-0" />}
        <span className="font-mono text-[11px] shrink-0">{Math.round(m.calories || 0)} kcal · P{Math.round(m.protein_g || 0)}</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {m.ingredients.map((ing, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="flex-1 min-w-0 truncate">{ing.item}</span>
              {editable
                ? <Input type="number" value={grams[i] ?? ''} onChange={e => setGrams(g => g.map((x, j) => j === i ? e.target.value : x))} data-testid={`nr-grams-${sid}-${i}`} className="w-20 h-7 font-mono text-center bg-regen-card border-[var(--c-hair)]" />
                : <span className="font-mono">{ing.grams}</span>}
              <span className="text-muted-foreground w-3">g</span>
            </div>
          ))}
          {editable && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={() => onSave(grams.map(g => parseFloat(g) || 0))} data-testid={`nr-save-${sid}`} className="flex-1 h-8 bg-[#FFB800] text-[#081726] text-xs"><Save className="w-3.5 h-3.5 mr-1" /> Save portions</Button>
              <Button size="sm" variant="outline" onClick={onSwap} data-testid={`nr-swap-${sid}`} className="h-8 border-[var(--c-hair)] text-xs"><ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Swap meal</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SwapDialog({ clientId, target, onClose, onDone }) {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (!target) return;
    setOpts(null);
    api.get(`/admin/clients/${clientId}/nutrition-review/swap-options`, { params: target }).then(r => setOpts(r.data.options)).catch(() => setOpts([]));
  }, [target]);
  const pick = async (o) => {
    try { const r = await api.post(`/admin/clients/${clientId}/nutrition-review/swap`, { ...target, meal_id: o.meal_id }); toast.success('Meal swapped'); onDone(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not swap'); }
  };
  return (
    <Dialog open={!!target} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[80vh] overflow-y-auto" data-testid="nr-swap-dialog">
        <DialogHeader><DialogTitle>Swap for an approved meal</DialogTitle></DialogHeader>
        {!opts ? <div className="text-sm text-muted-foreground">Loading…</div> : opts.length === 0 ? <div className="text-sm text-muted-foreground">No other approved meals fit this slot.</div> : (() => {
          const row = (o, i) => (
          <button key={o.meal_id} onClick={() => !o.blocked?.length && pick(o)} disabled={!!o.blocked?.length} data-testid={`nr-swap-opt-${i}`} className={`w-full text-left p-2.5 rounded-xl border border-[var(--c-hair)] text-sm ${o.blocked?.length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--c-film)]'}`}>
            <div className="font-semibold">{o.name}</div>
            <div className="text-[11px] font-mono text-muted-foreground">{Math.round(o.calories)} kcal · P{Math.round(o.protein_g)} → day {Math.round(o.day_totals.calories)} kcal / P{Math.round(o.day_totals.protein_g)}</div>
            {o.blocked?.length > 0 && <div className="text-[11px] text-[#FF5C5C] flex items-center gap-1" data-testid={`nr-swap-blocked-${i}`}><Ban className="w-3 h-3" />Blocked by fish rules: {o.blocked.join(', ')}</div>}
            {o.warnings.map((w, k) => <div key={k} className="text-[11px] text-[#FFB800]">{w}</div>)}
          </button>);
          const nb = opts.filter(o => !o.blocked?.length).length;
          return <>
            {opts.slice(0, nb).map(row)}
            {nb < opts.length && <details className="space-y-2" data-testid="nr-swap-blocked-group"><summary className="cursor-pointer text-xs text-[#FF5C5C] py-1">Blocked by fish rules ({opts.length - nb})</summary><div className="space-y-2 pt-2">{opts.slice(nb).map((o, j) => row(o, nb + j))}</div></details>}
          </>;
        })()}
      </DialogContent>
    </Dialog>
  );
}

function MealPlanStatus({ data, onRetry, busy }) {
  const failed = data.nutrition_failed;
  return (
    <div className="regen-card-elevated p-5 space-y-3" data-testid="nutrition-review-empty">
      <div className="label-mini text-[#FFB800]">Meal plan</div>
      {data.generating ? (
        <div className="flex items-center gap-2 text-sm text-[#00C8FF]" data-testid="nr-generating"><Loader2 className="w-4 h-4 animate-spin" /> Building the personalised meal plan…</div>
      ) : failed ? (
        <div className="flex items-start gap-2 text-sm rounded-xl border border-[#FF5C5C]/40 bg-[#FF5C5C]/10 p-3" data-testid="nr-failed">
          <AlertTriangle className="w-4 h-4 text-[#FF5C5C] shrink-0 mt-0.5" /><span>Meal plan generation failed: {failed.error || 'unknown error'}</span>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground" data-testid="nr-none">No personalised meal plan yet. Meal templates below are not a substitute for the client's plan.</div>
      )}
      {data.awaiting_review && !data.generating && (
        <Button onClick={onRetry} disabled={busy} data-testid="nr-generate-meal-plan" className="bg-[#FFB800] text-[#081726] font-semibold">
          {failed ? 'Generate meal plan again' : 'Generate meal plan'}
        </Button>
      )}
    </div>
  );
}

export function NutritionReview({ clientId, version }) {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(0);
  const [swap, setSwap] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/clients/${clientId}/nutrition-review`).then(r => setData(r.data)).catch(() => setData(null)), [clientId]);
  useEffect(() => { load(); }, [load, version]);
  useEffect(() => {
    if (!data?.generating) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.generating, load]);
  const retry = async () => {
    setBusy(true);
    try { await api.post(`/admin/clients/${clientId}/generate-meal-plan`); toast.success('Building the meal plan…'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start meal plan generation'); }
    setBusy(false);
  };
  if (!data) return null;
  if (!data.program) return <MealPlanStatus data={data} onRetry={retry} busy={busy} />;
  const save = async (w, d, index, grams) => {
    try { const r = await api.put(`/admin/clients/${clientId}/nutrition-review/portions`, { week: w, day: d, index, grams }); setData(r.data); toast.success('Portions saved — totals recalculated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const wk = data.weeks[week];
  return (
    <div className="regen-card-elevated p-5 space-y-4" data-testid="nutrition-review">
      <Summary data={data} />
      <Frequency data={data} />
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {data.weeks.map((w, i) => (
          <button key={w.week_number} onClick={() => setWeek(i)} data-testid={`nr-week-${w.week_number}`}
            className={`px-3 py-1.5 rounded-full text-sm border shrink-0 ${i === week ? 'bg-[#FFB800] text-[#081726] border-[#FFB800]' : 'border-[var(--c-hair)]'}`}>Week {w.week_number}</button>
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {wk?.days.map(d => (
          <div key={d.day_index} className="rounded-xl border border-[var(--c-hair)] p-2.5 space-y-1.5" data-testid={`nr-day-${wk.week_number}-${d.day_index}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold">{d.date ? format(parseISO(d.date), 'EEE d MMM') : DAY[d.day_index]}</span>
              <span className={`font-mono ${d.kcal_ok ? '' : 'text-[#FF5C5C]'}`} data-testid={`nr-day-total-${wk.week_number}-${d.day_index}`}>{d.totals.calories} kcal · <span className={d.protein_ok ? '' : 'text-[#FF5C5C]'}>P{d.totals.protein_g}</span></span>
            </div>
            {d.fish_issues?.length > 0 && <div className="text-[11px] text-[#FF5C5C]" data-testid={`nr-day-fish-${wk.week_number}-${d.day_index}`}>Fish rule broken: {d.fish_issues.join(', ')}</div>}
            {d.meals.map(m => (
              <MealRow key={m.meal_id} m={m} editable={data.editable} sid={`${wk.week_number}-${d.day_index}-${m.index}`}
                onSave={(g) => save(wk.week_number, d.day_index, m.index, g)}
                onSwap={() => setSwap({ week: wk.week_number, day: d.day_index, index: m.index })} />
            ))}
          </div>
        ))}
      </div>
      <SwapDialog clientId={clientId} target={swap} onClose={() => setSwap(null)} onDone={(d) => { setSwap(null); setData(d); }} />
    </div>
  );
}
